import { EXAM_RULES } from "@eb/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, PlayCircle, XCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Badge, Button, Card, EmptyState, ErrorBanner, Spinner } from "../components/ui";
import { api } from "../lib/api";
import { formatDateTime, formatDuration } from "../lib/format";

export function ExamPage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const examsQuery = useQuery({ queryKey: ["exams"], queryFn: api.exams.list });

    const createMutation = useMutation({
        mutationFn: api.exams.create,
        onSuccess: (session) => {
            void queryClient.invalidateQueries({ queryKey: ["exams"] });
            navigate(`/exam/${session.id}`);
        }
    });

    if (examsQuery.isLoading) return <Spinner />;
    if (examsQuery.isError || !examsQuery.data) {
        return <ErrorBanner message="加载模拟考记录失败" onRetry={() => void examsQuery.refetch()} />;
    }

    const { sessions, activeId } = examsQuery.data;
    const finished = sessions.filter((session) => session.finishedAt !== null);
    const passedCount = finished.filter((session) => session.passed).length;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold text-ink">模拟考</h1>
                <p className="mt-1 text-sm text-ink-soft">
                    按官方规则出题：{EXAM_RULES.federalQuestions} 道联邦题 + {EXAM_RULES.stateQuestions}{" "}
                    道州题，限时 {EXAM_RULES.durationMinutes} 分钟，答对 {EXAM_RULES.passingScore} 题通过。
                </p>
            </div>

            {activeId ? (
                <Card className="flex flex-wrap items-center justify-between gap-3 border-warn/40 bg-warn-soft p-5">
                    <div>
                        <p className="text-sm font-medium text-ink">你有一场还没做完的模拟考</p>
                        <p className="mt-0.5 text-sm text-ink-soft">计时仍在继续，回去把它做完吧。</p>
                    </div>
                    <Link to={`/exam/${activeId}`}>
                        <Button>继续考试</Button>
                    </Link>
                </Card>
            ) : (
                <Card className="flex flex-wrap items-center justify-between gap-4 p-5">
                    <div>
                        <h2 className="text-sm font-semibold text-ink">开始一场新的模拟考</h2>
                        <p className="mt-1 text-sm text-ink-soft">
                            交卷后每道题都会按作答情况进入你的复习计划，答错的会很快再出现。
                        </p>
                    </div>
                    <Button onClick={() => createMutation.mutate()} loading={createMutation.isPending}>
                        <PlayCircle className="size-4" aria-hidden />
                        开始考试
                    </Button>
                </Card>
            )}

            {createMutation.isError ? (
                <ErrorBanner message={(createMutation.error as Error).message} />
            ) : null}

            <section className="space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-ink">历史成绩</h2>
                    {finished.length > 0 ? (
                        <span className="text-sm text-ink-soft">
                            {finished.length} 次考试 · 通过 {passedCount} 次
                        </span>
                    ) : null}
                </div>

                {sessions.length === 0 ? (
                    <EmptyState title="还没有考试记录" description="做完第一场模拟考后，成绩会显示在这里。" />
                ) : (
                    <ul className="space-y-2">
                        {sessions.map((session) => (
                            <li key={session.id}>
                                <Link
                                    to={
                                        session.finishedAt
                                            ? `/exam/${session.id}/result`
                                            : `/exam/${session.id}`
                                    }
                                    className="flex items-center gap-3 rounded-xl border border-line bg-surface px-4 py-3 transition-colors hover:border-brand"
                                >
                                    {session.finishedAt === null ? (
                                        <Badge tone="warn">进行中</Badge>
                                    ) : session.passed ? (
                                        <CheckCircle2 className="size-5 shrink-0 text-good" aria-label="通过" />
                                    ) : (
                                        <XCircle className="size-5 shrink-0 text-bad" aria-label="未通过" />
                                    )}

                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm text-ink">
                                            {session.score === null
                                                ? "尚未交卷"
                                                : `${session.score} / ${session.total} 题`}
                                        </p>
                                        <p className="text-xs text-ink-soft">
                                            {formatDateTime(session.startedAt)}
                                            {session.durationSeconds !== null
                                                ? ` · 用时 ${formatDuration(session.durationSeconds)}`
                                                : ""}
                                        </p>
                                    </div>

                                    {session.passed !== null ? (
                                        <Badge tone={session.passed ? "good" : "bad"}>
                                            {session.passed ? "通过" : "未通过"}
                                        </Badge>
                                    ) : null}
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
